import { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { HelmetProvider } from 'react-helmet-async';

// Pages
import HomePage from '@/pages/HomePage';
import AuthPage from '@/pages/AuthPage';
import DirectoryPage from '@/pages/DirectoryPage';
import DashboardPage from '@/pages/DashboardPage';
import MesFichesPage from '@/pages/MesFichesPage';
import MesAdhesionsPage from '@/pages/MesAdhesionsPage';
import MesArticlesPage from '@/pages/MesArticlesPage';
import FormationsPage from '@/pages/FormationsPage';
import ArticlesPage from '@/pages/ArticlesPage';
import AdhesionPage from '@/pages/AdhesionPage';
import FormationDetailPage from '@/pages/FormationDetailPage';
import ArticleDetailPage from '@/pages/ArticleDetailPage';
import WcOrdersPage from '@/pages/WcOrdersPage';
import AdhesionCourrierPage from '@/pages/AdhesionCourrierPage';
import MyProfilePage from '@/pages/MyProfilePage';
import AnalyticsPage from '@/pages/AnalyticsPage';
import EventsManagementPage from '@/pages/EventsManagementPage';
import BlogManagementPage from '@/pages/BlogManagementPage';
import AdminDashboardHome from '@/pages/AdminDashboardHome';
import PatientContentPage from '@/pages/PatientContentPage';
import AdBannersPage from '@/pages/AdBannersPage';
import PatientsHomePage from '@/pages/PatientsHomePage';
import PatientArticlePage from '@/pages/PatientArticlePage';
import PatientCategoryPage from '@/pages/PatientCategoryPage';
import PatientFAQPage from '@/pages/PatientFAQPage';
import NotificationsManagementPage from '@/pages/NotificationsManagementPage';
import ThemeManagementPage from '@/pages/ThemeManagementPage';
import AboutPage from '@/pages/AboutPage';
import PartnersPage from '@/pages/PartnersPage';
import ContactPage from '@/pages/ContactPage';
import FAQPage from '@/pages/FAQPage';
import SiteTextPage from '@/pages/SiteTextPage';
import PagesManagementPage from '@/pages/PagesManagementPage';
import PageEditorPage from '@/pages/PageEditorPage';
import PageBuilderPage from '@/pages/PageBuilderPage';
import DynamicPage from '@/pages/DynamicPage';
import SecureArticleViewerPage from '@/pages/SecureArticleViewerPage';
import SettingsPage from '@/pages/SettingsPage';
import MonComptePage from '@/pages/MonComptePage';
import FormationsManagementPage from '@/pages/FormationsManagementPage';
import RenewalRemindersPage from '@/pages/RenewalRemindersPage';
import HomepageCMSPage from '@/pages/HomepageCMSPage';
import InstitutionalCMSPage from '@/pages/InstitutionalCMSPage';
import ArticlesManagementPage from '@/pages/ArticlesManagementPage';
import RegionalPageManagement from '@/pages/RegionalPageManagement';
import MembersCentralPage from '@/pages/MembersCentralPage';
import UniversalLayout from '@/components/UniversalLayout';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';
import SupportPage from '@/pages/SupportPage';
import BureauNationalPage from '@/pages/BureauNationalPage';
import RecusFiscauxPage from '@/pages/RecusFiscauxPage';
import MaintenancePage from '@/pages/MaintenancePage';
import PublicEventsPage from '@/pages/PublicEventsPage';
import EventDetailPage from '@/pages/EventDetailPage';
import PublicBlogPage from '@/pages/PublicBlogPage';
import PublicBlogDetailPage from '@/pages/PublicBlogDetailPage';
import PublicBureauPage from '@/pages/PublicBureauPage';
import PublicActuPage from '@/pages/PublicActuPage';
import PublicFormationsPage from '@/pages/PublicFormationsPage';
import PractitionerProfilePage from '@/pages/PractitionerProfilePage';
import PublicFormationDetailPage from '@/pages/PublicFormationDetailPage';
import IncidentDeclarationPage from '@/pages/IncidentDeclarationPage';
import RegionPage from '@/pages/RegionPage';
import RegionalBureauPage from '@/pages/RegionalBureauPage';
import PaymentConfirmationPage from '@/pages/PaymentConfirmationPage';
import ForumPage from '@/pages/ForumPage';
import ForumTopicPage from '@/pages/ForumTopicPage';
import MemberMessagingPage from '@/pages/MemberMessagingPage';
import AdminMessagingPage from '@/pages/AdminMessagingPage';
import ElectionsManagementPage from '@/pages/ElectionsManagementPage';
import VotePage from '@/pages/VotePage';
import LinkedInActualitePage from '@/pages/LinkedInActualitePage';
import FichesAnnuairePage from '@/pages/FichesAnnuairePage';
import ForumModerationPage from '@/pages/ForumModerationPage';
import HelloAssoAdminPage from '@/pages/HelloAssoAdminPage';
import SupportTicketsAdminPage from '@/pages/SupportTicketsAdminPage';
import SupportTechPage from '@/pages/SupportTechPage';
import ImpersonationAuditPage from '@/pages/ImpersonationAuditPage';
import ReleaseNotesPage from '@/pages/ReleaseNotesPage';
import RolesPermissionsPublicPage from '@/pages/RolesPermissionsPublicPage';
import RolesPermissionsAdminPage from '@/pages/RolesPermissionsAdminPage';
import EmailSettingsPage from '@/pages/EmailSettingsPage';
import UpdateAvailableBanner from '@/components/UpdateAvailableBanner';
import MesSupportPage from '@/pages/MesSupportPage';
import PaiementsPage from '@/pages/PaiementsPage';
import CopilotPage from '@/pages/CopilotPage';
import CreditsPage from '@/pages/CreditsPage';
import MemberCopilotPage from '@/pages/MemberCopilotPage';
import MemberVotesPage from '@/pages/MemberVotesPage';
import AuditLogPage from '@/pages/AuditLogPage';
import SiteOverviewPage from '@/pages/SiteOverviewPage';
import SitePageEditorPage from '@/pages/SitePageEditorPage';
import WeeklySummaryPage from '@/pages/WeeklySummaryPage';
import SecurityWatchPage from '@/pages/SecurityWatchPage';
import { HiddenPageGate } from '@/components/HiddenPageGate';
import { clearFileSession, refreshFileSession } from '@/lib/fileSession';
import QuickAccessFab from '@/components/QuickAccessFab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Axios interceptor for auth
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      clearFileSession();
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/auth';
    }
    return Promise.reject(error);
  }
);

function ProtectedRoute({ children, allowedRoles }) {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const token = localStorage.getItem('token');

  if (!token || !user) {
    return <Navigate to="/auth" />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    toast.error('Accès non autorisé');
    return <Navigate to="/dashboard" />;
  }

  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function ImpersonationBanner() {
  const isImpersonating = localStorage.getItem('impersonating') === 'true';
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  const exitImpersonation = () => {
    const supportToken = localStorage.getItem('support_token');
    const supportUser = localStorage.getItem('support_user');
    if (supportToken && supportUser) {
      localStorage.setItem('token', supportToken);
      localStorage.setItem('user', supportUser);
    }
    localStorage.removeItem('support_token');
    localStorage.removeItem('support_user');
    localStorage.removeItem('impersonating');
    refreshFileSession().finally(() => { window.location.href = '/support-tech'; });
  };

  if (!isImpersonating || !user) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] bg-orange-500 text-white py-1.5 px-4 flex items-center justify-center gap-3 text-sm font-medium shadow-lg" data-testid="impersonation-banner">
      <span>Connecté en tant que <strong>{user.first_name} {user.last_name}</strong> ({user.role})</span>
      <button onClick={exitImpersonation} className="px-3 py-0.5 bg-white text-orange-600 rounded-full text-xs font-bold hover:bg-orange-100 transition-colors">
        Quitter
      </button>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  return (
    <HelmetProvider>
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <ImpersonationBanner />
        <UpdateAvailableBanner />
        <UniversalLayout>
          <HiddenPageGate>
          <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/annuaire" element={<DirectoryPage />} />
          <Route path="/annuaire/:id" element={<PractitionerProfilePage />} />
          <Route path="/adhesion" element={<AdhesionPage />} />
          <Route path="/adhesion/courrier" element={<AdhesionCourrierPage />} />
          <Route path="/adhesion/confirmation" element={<PaymentConfirmationPage />} />
          <Route path="/forum" element={<Navigate to="/messagerie" replace />} />
          <Route path="/forum/:topicId" element={<Navigate to="/messagerie" replace />} />
          <Route path="/messagerie" element={
            <ProtectedRoute allowedRoles={['ophthalmologist', 'intern', 'super_admin', 'regional', 'tresorier', 'auteur']}>
              <MemberMessagingPage />
            </ProtectedRoute>
          } />
          <Route path="/votes" element={
            <ProtectedRoute allowedRoles={['ophthalmologist', 'intern', 'super_admin', 'regional', 'tresorier', 'auteur']}>
              <MemberVotesPage />
            </ProtectedRoute>
          } />
          <Route path="/votes/:electionId" element={
            <ProtectedRoute allowedRoles={['ophthalmologist', 'intern', 'super_admin', 'regional', 'tresorier', 'auteur']}>
              <MemberVotesPage />
            </ProtectedRoute>
          } />
          <Route path="/declaration-incident" element={<IncidentDeclarationPage />} />
          <Route path="/roles-permissions" element={<RolesPermissionsPublicPage />} />
          <Route path="/credits" element={
            <ProtectedRoute allowedRoles={['ophthalmologist', 'intern', 'super_admin', 'regional', 'tresorier', 'auteur']}>
              <CreditsPage />
            </ProtectedRoute>
          } />
          <Route path="/credits/success" element={
            <ProtectedRoute allowedRoles={['ophthalmologist', 'intern', 'super_admin', 'regional', 'tresorier', 'auteur']}>
              <CreditsPage />
            </ProtectedRoute>
          } />
          <Route path="/credits/cancel" element={<Navigate to="/credits" replace />} />
          <Route path="/copilot" element={
            <ProtectedRoute allowedRoles={['ophthalmologist', 'intern', 'super_admin', 'regional', 'tresorier', 'auteur']}>
              <MemberCopilotPage />
            </ProtectedRoute>
          } />
          
          {/* Public Patient Routes */}
          <Route path="/patients" element={<PatientsHomePage />} />
          <Route path="/espace-patients" element={<PatientsHomePage />} />
          <Route path="/patients/article/:slug" element={<PatientArticlePage />} />
          <Route path="/espace-patients/article/:slug" element={<PatientArticlePage />} />
          <Route path="/patients/categorie/:slug" element={<PatientCategoryPage />} />
          <Route path="/espace-patients/categorie/:slug" element={<PatientCategoryPage />} />
          <Route path="/patients/faq" element={<PatientFAQPage />} />
          <Route path="/espace-patients/faq" element={<PatientFAQPage />} />
          
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/mon-profil"
            element={
              <ProtectedRoute>
                <MyProfilePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/mes-fiches"
            element={
              <ProtectedRoute allowedRoles={['ophthalmologist', 'super_admin']}>
                <MesFichesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/mes-adhesions"
            element={
              <ProtectedRoute>
                <MesAdhesionsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/mon-support"
            element={
              <ProtectedRoute>
                <MesSupportPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/support-tech"
            element={
              <ProtectedRoute>
                <SupportTechPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/support-tech/audit"
            element={
              <ProtectedRoute>
                <ImpersonationAuditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/release-notes"
            element={
              <ProtectedRoute>
                <ReleaseNotesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/audit-log"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AuditLogPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/site-overview"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'regional']}>
                <SiteOverviewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/site-pages/edit/:section"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'regional']}>
                <SitePageEditorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/securite"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'support']}>
                <SecurityWatchPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/resume-hebdo"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'regional', 'tresorier', 'auteur', 'support']}>
                <WeeklySummaryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/roles-permissions"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'regional', 'tresorier', 'auteur']}>
                <RolesPermissionsAdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/email-settings"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <EmailSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/copilot"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <CopilotPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/student-cards"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'regional']}>
                <MembersCentralPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/mes-articles"
            element={
              <ProtectedRoute allowedRoles={['ophthalmologist', 'super_admin']}>
                <MesArticlesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/mes-formations-auteur"
            element={
              <ProtectedRoute allowedRoles={['ophthalmologist', 'super_admin']}>
                <MesArticlesPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/formations"
            element={
              <ProtectedRoute allowedRoles={['ophthalmologist', 'intern', 'super_admin', 'regional']}>
                <FormationsPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/formations/:id"
            element={
              <ProtectedRoute allowedRoles={['ophthalmologist', 'intern', 'super_admin', 'regional']}>
                <FormationDetailPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/articles"
            element={
              <ProtectedRoute allowedRoles={['ophthalmologist', 'intern', 'super_admin', 'regional']}>
                <ArticlesPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/articles/:id"
            element={
              <ProtectedRoute allowedRoles={['ophthalmologist', 'intern', 'super_admin', 'regional']}>
                <ArticleDetailPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'regional']}>
                <AdminDashboardHome />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/notifications"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'regional']}>
                <NotificationsManagementPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/members"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'regional']}>
                <MembersCentralPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/patient-content"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'regional']}>
                <PatientContentPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/ad-banners"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AdBannersPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/paiements"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'regional', 'tresorier']}>
                <PaiementsPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/my-profile"
            element={
              <ProtectedRoute allowedRoles={['ophthalmologist']}>
                <MyProfilePage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'regional']}>
                <AnalyticsPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/events"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'regional']}>
                <EventsManagementPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/blog"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'regional']}>
                <BlogManagementPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/theme"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <ThemeManagementPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/pages"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'regional']}>
                <PagesManagementPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/pages/:slug"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'regional']}>
                <PageEditorPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/page-builder/:slug"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'regional']}>
                <PageBuilderPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SettingsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/mon-compte"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'regional', 'ophthalmologist', 'intern', 'auteur', 'tresorier']}>
                <MonComptePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/formations"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'regional', 'auteur']}>
                <FormationsManagementPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/renewal-reminders"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <RenewalRemindersPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/homepage-cms"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <HomepageCMSPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/institutional-cms"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <InstitutionalCMSPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/articles"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'regional', 'auteur']}>
                <ArticlesManagementPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/regional"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'regional']}>
                <RegionalPageManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/support"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'maintenance']}>
                <SupportTicketsAdminPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/maintenance"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'maintenance']}>
                <MaintenancePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/bureau"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <BureauNationalPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/bureaux-regionaux"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <RegionalBureauPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/recus-fiscaux"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'regional']}>
                <RecusFiscauxPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/directory"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'regional']}>
                <FichesAnnuairePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/elections"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'regional']}>
                <ElectionsManagementPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/actualite"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <LinkedInActualitePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/forum-moderation"
            element={<Navigate to="/admin/messaging" replace />}
          />
          <Route
            path="/admin/messaging"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'maintenance']}>
                <AdminMessagingPage />
              </ProtectedRoute>
            }
          />

          {/* Admin catch-all: redirect unknown admin routes to dashboard */}
          <Route path="/admin/*" element={<Navigate to="/admin" replace />} />

          {/* Secure Article Viewer */}
          <Route
            path="/articles/:articleId/view"
            element={
              <ProtectedRoute>
                <SecureArticleViewerPage />
              </ProtectedRoute>
            }
          />

          {/* Public Pages */}
          <Route path="/pages/:slug" element={<DynamicPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/a-propos" element={<AboutPage />} />
          <Route path="/qui-sommes-nous" element={<AboutPage />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/legal" element={<SiteTextPage section="page_legal" />} />
          <Route path="/privacy" element={<SiteTextPage section="page_privacy" />} />
          <Route path="/cookies" element={<SiteTextPage section="page_privacy" />} />
          <Route path="/conditions" element={<SiteTextPage section="page_conditions" />} />
          <Route path="/conditions-annuaire" element={<SiteTextPage section="page_conditions_annuaire" />} />
          <Route path="/events" element={<PublicEventsPage />} />
          <Route path="/agenda" element={<PublicEventsPage />} />
          <Route path="/events/:id" element={<EventDetailPage />} />
          <Route path="/agenda/:id" element={<EventDetailPage />} />
          <Route path="/blog" element={<PublicBlogPage />} />
          <Route path="/blog/:slug" element={<PublicBlogDetailPage />} />
          <Route path="/actu" element={<PublicActuPage />} />
          <Route path="/bureau" element={<PublicBureauPage />} />
          <Route path="/nos-formations" element={<PublicFormationsPage />} />
          <Route path="/nos-formations/:id" element={<PublicFormationDetailPage />} />
          <Route path="/nos-replays" element={<PublicFormationsPage />} />
          {/* Unified Publications & Formations — same component */}
          <Route path="/publications" element={<PublicFormationsPage />} />
          <Route path="/publications/:id" element={<PublicFormationDetailPage />} />
          <Route path="/region/:slug" element={<RegionPage />} />
          </Routes>
          </HiddenPageGate>
          <QuickAccessFab />
        </UniversalLayout>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      <PWAInstallPrompt />
    </div>
    </HelmetProvider>
  );
}

export default App;
